import { useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

export type TrendSession = {
  created_at: string;
  wpm: number;
  filler_count: number;
  star_score: number;
};

type Tone = "success" | "warning" | "error" | "info";

type SparkProps = {
  values: number[];
  width?: number;
  height?: number;
  color: string;
  band?: { lo: number; hi: number; max: number };
  yMin?: number;
  yMax?: number;
};

function Sparkline({ values, width = 180, height = 56, color, band, yMin, yMax }: SparkProps) {
  const ref = useRef<SVGSVGElement | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!ref.current) return;
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    if (values.length === 0) return;

    const padX = 4;
    const padY = 6;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    const x = d3
      .scaleLinear()
      .domain([0, Math.max(1, values.length - 1)])
      .range([padX, padX + innerW]);

    const lo = yMin ?? Math.min(...values);
    const hi = yMax ?? Math.max(...values);
    const pad = (hi - lo) * 0.15 || 1;
    const y = d3
      .scaleLinear()
      .domain([lo - pad, hi + pad])
      .range([padY + innerH, padY]);

    // Optional target band (e.g., WPM 130-160)
    if (band) {
      const bandTop = y(Math.min(band.hi, band.max));
      const bandBottom = y(Math.max(band.lo, lo - pad));
      svg
        .append("rect")
        .attr("x", padX)
        .attr("y", bandTop)
        .attr("width", innerW)
        .attr("height", Math.max(0, bandBottom - bandTop))
        .attr("fill", color)
        .attr("opacity", 0.1);
    }

    const line = d3
      .line<number>()
      .x((_, i) => x(i))
      .y((d) => y(d))
      .curve(d3.curveMonotoneX);

    if (values.length > 1) {
      svg
        .append("path")
        .datum(values)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 1.75)
        .attr("stroke-linecap", "round")
        .attr("d", line as never);
    }

    // Dots for each point (subtle), highlight last
    svg
      .selectAll("circle.point")
      .data(values)
      .enter()
      .append("circle")
      .attr("class", "point")
      .attr("cx", (_, i) => x(i))
      .attr("cy", (d) => y(d))
      .attr("r", (_, i) => (i === values.length - 1 ? 3 : 1.5))
      .attr("fill", (_, i) =>
        i === values.length - 1 ? color : theme.palette.text.disabled,
      );
  }, [values, width, height, color, band, yMin, yMax, theme.palette.text.disabled]);

  return (
    <svg
      ref={ref}
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-hidden
      style={{ display: "block", maxWidth: "100%" }}
    />
  );
}

type StatCardProps = {
  label: string;
  values: number[];
  unit?: string;
  format?: (n: number) => string;
  color: string;
  tone: Tone;
  hint: string;
  band?: { lo: number; hi: number; max: number };
  lowerIsBetter?: boolean;
  yMin?: number;
  yMax?: number;
};

function StatCard({
  label,
  values,
  unit,
  format,
  color,
  tone,
  hint,
  band,
  lowerIsBetter,
  yMin,
  yMax,
}: StatCardProps) {
  const fmt = format ?? ((n: number) => Math.round(n).toString());
  const last = values[values.length - 1];
  const prev = values.length >= 2 ? values[values.length - 2] : null;
  const delta = prev != null ? last - prev : null;
  const improving =
    delta == null
      ? null
      : lowerIsBetter
        ? delta < 0
        : delta > 0;

  const deltaColor =
    improving == null
      ? "text.secondary"
      : improving
        ? "success.main"
        : "error.main";
  const deltaSign = delta == null ? "" : delta > 0 ? "+" : "";

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="caption" color={`${tone}.main`} sx={{ fontWeight: 600 }}>
            {tone === "success" ? "On target" : tone === "warning" ? "Close" : tone === "error" ? "Off" : "—"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {fmt(last)}
            {unit ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {unit}
              </Typography>
            ) : null}
          </Typography>
          {delta != null && (
            <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600 }}>
              {deltaSign}
              {fmt(delta)} vs prev
            </Typography>
          )}
        </Stack>
        <Box sx={{ mt: 1 }}>
          <Sparkline values={values} color={color} band={band} yMin={yMin} yMax={yMax} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {hint}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function TrendSparklines({ sessions }: { sessions: TrendSession[] }) {
  const theme = useTheme();

  // Sort oldest -> newest for trend reading
  const sorted = useMemo(
    () =>
      [...sessions].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      ),
    [sessions],
  );

  if (sorted.length === 0) return null;

  if (sorted.length < 2) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Trends
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Record at least one more session to see WPM, filler and STAR trends.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const wpmVals = sorted.map((s) => Number(s.wpm) || 0);
  const fillerVals = sorted.map((s) => Number(s.filler_count) || 0);
  const starVals = sorted.map((s) => Number(s.star_score) || 0);

  const lastWpm = wpmVals[wpmVals.length - 1];
  const wpmTone: Tone =
    lastWpm >= 130 && lastWpm <= 160 ? "success" : lastWpm >= 110 && lastWpm <= 180 ? "warning" : "error";

  const avgFiller = fillerVals.reduce((a, b) => a + b, 0) / fillerVals.length;
  const lastFiller = fillerVals[fillerVals.length - 1];
  const fillerTone: Tone =
    lastFiller <= 3 ? "success" : lastFiller <= avgFiller ? "warning" : "error";

  const lastStar = starVals[starVals.length - 1];
  const starTone: Tone =
    lastStar >= 4 ? "success" : lastStar >= 2.5 ? "warning" : "error";

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ pl: 0.5 }}>
        Trends across {sorted.length} sessions
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            label="Words / minute"
            values={wpmVals}
            color={theme.palette.primary.main}
            tone={wpmTone}
            hint="Target band 130–160 wpm"
            band={{ lo: 130, hi: 160, max: 200 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            label="Filler words"
            values={fillerVals}
            color={theme.palette.warning.main}
            tone={fillerTone}
            hint="Lower is better — aim for ≤3"
            lowerIsBetter
            yMin={0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            label="STAR score"
            values={starVals}
            unit="/5"
            format={(n) => n.toFixed(1)}
            color={theme.palette.success.main}
            tone={starTone}
            hint="Higher is better — aim for ≥4"
            yMin={0}
            yMax={5}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
